import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { CommonEnums } from 'dashboard-services/api/common';
import { DataScienceEnums, DataScienceQuery } from 'dashboard-services/api/dataScience';
import Query from 'dashboard-services/api/Query';
import { CommonService, ReduxService } from 'dashboard-services/services';
import { globalActions } from 'primary-components';

import getApiConfig from 'api/ApiConfig';

import { useInfiniteQuery, useMutation, useQueries, useQuery } from '@tanstack/react-query';

export default(() => {
  const useGetPipelineDetails = ({ getQueryBuilder, pipelineId, enabled = false, shouldBeLimited = undefined, refetchOnReconnect = true, refetchOnWindowFocus = true, ...keys }) => (
    useQuery({
      ...DataScienceQuery.QUERIES.getPipelineDetails(
        getQueryBuilder, { pipelineId, ...keys }
      ),
      enabled,
      retry: 0,
      refetchInterval: shouldBeLimited !== undefined ? () => shouldBeLimited() ? undefined : 30_000 : undefined,
      refetchOnReconnect,
      refetchOnWindowFocus
    })
  )

  const useRunPipeline = ({ getQueryBuilder, pipelineId, details = {} }) => (
    useMutation({
      ...DataScienceQuery.QUERIES.executePipeline(
        getQueryBuilder, { 
          pipelineId, 
          details 
        }
      )
    })
  )

  const useContinuePipeline = ({ getQueryBuilder, jobId }) => (
    useMutation({
      ...DataScienceQuery.QUERIES.resumePipeline(
        getQueryBuilder,
        {
          jobId
        }
      )
    })
  )

  const useStopPipeline = ({ getQueryBuilder, pipelineId, jobId }) => (
    useMutation({
      ...DataScienceQuery.QUERIES.stopPipeline(
        getQueryBuilder,
        {
          pipelineId,
          jobId
        }
      )
    })
  )

  const useSearchPipelines = ({ query, triggerType, getQueryBuilder, shouldBeLimited, latest = true, size = CommonService.getRequestSize(), sortBy =  DataScienceEnums.PIPELINE_SORT_BY.NAME, sortOrder = CommonEnums.SORT_ORDER.ASCENDING, shouldRefetch = true, ...rest }) => (
    useInfiniteQuery({
      ...DataScienceQuery.QUERIES.searchPipelines(
        getQueryBuilder,
        {
          latest,
          query,
          size,
          sortBy,
          sortOrder,
          triggerType
        }
      ),
      initialPageParam: 0,
      refetchInterval: () => !shouldRefetch || shouldBeLimited() ? undefined : 10000,
      getNextPageParam: CommonService.getNextPageParam,
      ...rest
    })
  )

  const useSearchPipelinesMutation = ({ getQueryBuilder, from, size, latest, query, sortBy, sortOrder, triggerType, ...rest }) =>
    useMutation({
      ...DataScienceQuery.QUERIES.searchPipelinesMutation(
        getQueryBuilder,
        {
          from,
          latest,
          query,
          size,
          sortBy,
          sortOrder,
          triggerType
        }
      ),
      ...rest
    })

  const usePipelineVersions = ({ pipelineName, groupName, query, size = CommonService.getRequestSize(), getQueryBuilder, shouldBeLimited }) =>
    useInfiniteQuery({
      ...DataScienceQuery.QUERIES.getPipelineVersions(
        getQueryBuilder,
        {
          pipelineName,
          groupName,
          size,
          query
        }
      ),
      initialPageParam: 0,
      refetchInterval: () => shouldBeLimited() ? undefined : 10000,
      getNextPageParam: CommonService.getNextPageParam
    })
    
  const useFavoritePipelines = ({ favoritePipelines, getQueryBuilder, shouldBeLimited, userName }) => {
    const queries = useMemo(() => favoritePipelines.map(fav => ({
            ...DataScienceQuery.QUERIES.getPipelineVersionsById(
              getQueryBuilder,
              {
                pipelineId: fav.id,
                favorite: true,
                size: 1
              },
            ),
            enabled: !!userName && !!fav.id,
            refetchInterval: () => shouldBeLimited() ? undefined : 10000,
            refetchOnWindowFocus: false
          })), [favoritePipelines, getQueryBuilder, shouldBeLimited, userName]),
          combine = useCallback(results => ({
            data: results.map(result => result.data).map(v => v?.items?.[0]).filter(item => item?.id !== undefined).map(g => ReduxService.updateObject(g, { favorite: true })),
            isPending: results.some(result => result.isPending),
          }), [])
    return useQueries({
      queries,
      combine
    })
  }

  const useDeletePipeline = ({ pipelineId, getQueryBuilder, queryClient }) =>
    useMutation({
      ...DataScienceQuery.QUERIES.deletePipeline(
        getQueryBuilder,
        {
          pipelineId
        }
      ),
      onSuccess: () => queryClient.invalidateQueries({ queryKey: DataScienceQuery.KEYS.SEARCH_PIPELINES() }),
    })

  const useGetPipelineTriggers = ({ getQueryBuilder, pipelineId, size }) =>
    useInfiniteQuery({
      ...DataScienceQuery.QUERIES.getPipelineSchedules(
        getQueryBuilder, 
        { 
          pipelineId, 
          size 
        }
      ),
      enabled: !!pipelineId,
      initialPageParam: 0,
      getNextPageParam: CommonService.getNextPageParam
    })

  const usePatchPipeline = ({ getQueryBuilder, pipelineId, objToUpdate, queryClient }) =>
    useMutation({
      ...DataScienceQuery.QUERIES.patchPipeline(
        getQueryBuilder,
        {
          pipelineId,
          objToUpdate
        },
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: DataScienceQuery.KEYS.SEARCH_PIPELINES() })
        queryClient.invalidateQueries({ queryKey: DataScienceQuery.KEYS.GET_PIPELINE_DETAILS() })
        queryClient.invalidateQueries({ queryKey: DataScienceQuery.KEYS.GET_PIPELINE_VERSIONS_BY_ID() })
      }
    })

  const useSchedulePipeline = ({ getQueryBuilder, queryClient, pipelineId, schedules }) =>
    useMutation({
      ...DataScienceQuery.QUERIES.schedulePipeline(
        getQueryBuilder,
        {
          pipelineId,
          schedulerTriggers: schedules
        }
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: DataScienceQuery.KEYS.SEARCH_PIPELINES() })
        queryClient.invalidateQueries({ queryKey: DataScienceQuery.KEYS.GET_PIPELINE_DETAILS() })
        queryClient.invalidateQueries({ queryKey: DataScienceQuery.KEYS.GET_PIPELINE_SCHEDULES() })
      }
    })

  const useGetCategories = ({ getQueryBuilder, enabled = true }) =>
    useQuery({
      ...DataScienceQuery.QUERIES.getTasksCategories(
        getQueryBuilder
      ),
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      gcTime: 60 * 60 * 1000, // 1hour,
      staleTime: 60 * 60 * 1000, // 1hour,
      enabled
    })

  const useGetParameters = ({ getQueryBuilder, jobId, pipelineId, shouldBeLimited, isExecuting }) =>
    useQuery({
      ...DataScienceQuery.QUERIES.getPipelineGeneratedValues(
        getQueryBuilder,
        {
          jobId,
          pipelineId
        }
      ),
      enabled: !!pipelineId && !!jobId,
      refetchInterval: () => shouldBeLimited() ? undefined : (isExecuting ? 3_000 : 10_000)
    })

  const useGetPipelineVersionsById = ({ getQueryBuilder, pipelineId, shouldBeLimited, select = undefined, ...keys }) =>
    useInfiniteQuery({
      ...DataScienceQuery.QUERIES.getPipelineVersionsById(
        getQueryBuilder,
        {
          pipelineId,
          size: 100,
          ...keys
        }
      ),
      refetchInterval: shouldBeLimited !== undefined ? () => shouldBeLimited() ? undefined : 30_000 : undefined,
      enabled: !!pipelineId,
      initialPageParam: 0,
      getNextPageParam: CommonService.getNextPageParam,
      select
    })

  const pipelineTagsSelect = (response = []) => response.map(item => ({ value: item, label: item }))
  const usePipelineTags = ({ getQueryBuilder, query }) =>
    useQuery({
      ...DataScienceQuery.QUERIES.getPipelineTags(
        getQueryBuilder,
        {
          query: query ? `.{0,}${query}.{0,}` : "*",
          sortOrder: CommonEnums.SORT_ORDER.ASCENDING,
        }
      ),
      select: pipelineTagsSelect
    })

  const useResumePipeline = ({ getQueryBuilder, jobId }) =>
    useMutation({
      ...DataScienceQuery.QUERIES.resumePipeline(
        getQueryBuilder,
        {
          jobId
      })
    })

  const useReExecutePipeline = ({ getQueryBuilder, pipelineId, jobId }) => useMutation({
    ...DataScienceQuery.QUERIES.reExecutePipeline(
      getQueryBuilder,
      {
        pipelineId,
        jobId
      }
    )
  })

  const useCreatePipeline = ({ getQueryBuilder }) =>
    useMutation({
      ...DataScienceQuery.QUERIES.createPipelineSimplified(
        getQueryBuilder,
        {
          
        }
      )
    })
  
  const useUpdatePipeline = ({ getQueryBuilder }) =>
    useMutation({
      ...DataScienceQuery.QUERIES.updatePipelineSimplified(
        getQueryBuilder,
      )
    })

  const useGetPipelineByIdQuery = ({ getQueryBuilder, pipelineId, enabled = true, select, ...options }) => 
    useQuery({
      ...DataScienceQuery.QUERIES.getPipelineById(
        getQueryBuilder,
        {
          pipelineId
        }
      ),
      enabled: !!pipelineId && enabled,
      select,
      ...options
    })

  const useGetPipelineById = ({ getQueryBuilder, pipelineId }) => 
    useMutation({
      ...DataScienceQuery.QUERIES.getPipelineByIdMutation(
        getQueryBuilder,
        {
          pipelineId
        }
      )
    })

  const useGetPipeline = ({ getQueryBuilder, pipelineName, groupName, version, enabled = true, ...options }) => 
    useQuery({
      ...DataScienceQuery.QUERIES.getPipeline(
        getQueryBuilder,
        {
          pipelineName,
          groupName,
          version
        }
      ),
      ...options,
      enabled: !!groupName && !!pipelineName && !!version && enabled,
    })

  const useGetPipelineMutation = ({ getQueryBuilder }) => 
    useMutation({
      ...DataScienceQuery.QUERIES.getPipelineMutation(
        getQueryBuilder
      )
    })

  const useSavePipelineCore = ({ setErrors } = {}) => {
    const dispatch = useDispatch(),
          getQueryBuilder = useCallback(() => 
            new Query.Builder(dispatch(getApiConfig()))
              .withShouldTimeOut(false)
              .withOnError(({ response = {}, message }) => {
                if(message) {
                  dispatch(globalActions.getMessageStore()).error(message)
                }
                if(!response) {
                  return;
                }
                const errors = response.validationErrors || []
                if(setErrors !== undefined) {
                  const formErrors = (errors || []).filter((e = {}) => e.field === "pipeline.name" || e.field === "pipeline.groupName" || (String(e.field).includes("pipeline.tags")))
                  const differentErrors = (errors || []).filter((e = {}) => e.field !== "pipeline.name" && e.field !== "pipeline.groupName" && !(String(e.field).includes("pipeline.tags")))
                  if(differentErrors && differentErrors.length > 0) {
                    differentErrors.forEach(e => dispatch(globalActions.getMessageStore()).error(e.message))
                  }
                  setErrors(formErrors)
                } else {
                  errors.forEach(e => dispatch(globalActions.getMessageStore()).error(e.message))
                }
              })
          , [dispatch, setErrors]),
          { mutateAsync: createPipelineCall, isPending: isSavePending } = useCreatePipeline({ getQueryBuilder }),
          { mutateAsync: updatePipelineCall, isPending: isUpdatePending } = useUpdatePipeline({ getQueryBuilder })
    return {
      createPipelineCall,
      updatePipelineCall,
      isSavePending,
      isUpdatePending
    }
  }

  const useExportPipeline = ({ pipelineId, getQueryBuilder }) =>
    useMutation({
      ...DataScienceQuery.QUERIES.exportPipeline(getQueryBuilder,
        {
          pipelineId
        }
      )
    })

  const useApprovePipeline = ({ getQueryBuilder, token, jobId }) =>
    useMutation({
      ...DataScienceQuery.QUERIES.approvePipeline(
        getQueryBuilder,
        {
          token,
          jobId
        }
      )
    })

  const useDeclinePipeline = ({ getQueryBuilder, token, jobId }) =>
    useMutation({
      ...DataScienceQuery.QUERIES.declinePipeline(
        getQueryBuilder,
        {
          token,
          jobId
        }
      )
    })

  const useSaveGeneratedValue = ({ getQueryBuilder, jobId, name, pipelineId, value }) =>
    useMutation({
      ...DataScienceQuery.QUERIES.savePipelineGeneratedValue(
        getQueryBuilder,
        {
          jobId,
          name,
          pipelineId,
          value
        }
      )
    })

  const useGetPipelineTags = ({ getQueryBuilder, query, sortOrder }) =>
    useQuery({
      ...DataScienceQuery.QUERIES.getPipelineTags(
        getQueryBuilder,
        {
          query,
          sortOrder
        }
      )
    })

  const useGetFileUsage = ({ getQueryBuilder, name, groupName, fileType, enabled = true, ...options }) =>
    useQuery({
      ...DataScienceQuery.QUERIES.getFileUsage(
        getQueryBuilder,
        {
          name,
          groupName,
          fileType
        }
      ),
      enabled: !!name && !!groupName && enabled,
      ...options
    })

  return {
    useGetPipelineDetails,
    useContinuePipeline,
    useStopPipeline,
    useSearchPipelines,
    useSearchPipelinesMutation,
    usePipelineVersions,
    useFavoritePipelines,
    useDeletePipeline,
    useGetPipelineTriggers,
    usePatchPipeline,
    useSchedulePipeline,
    useGetCategories,
    useGetParameters,
    useGetPipelineVersionsById,
    usePipelineTags,
    useResumePipeline,
    useRunPipeline,
    useReExecutePipeline,
    useCreatePipeline,
    useUpdatePipeline,
    useSavePipelineCore,
    useGetPipelineById,
    useExportPipeline,
    useGetPipelineByIdQuery,
    useApprovePipeline,
    useDeclinePipeline,
    useSaveGeneratedValue,
    useGetPipelineMutation,
    useGetPipeline,
    useGetPipelineTags,
    useGetFileUsage
  }
})()